import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, BookOpen, ShieldCheck, ClipboardList, LogOut, Star, MapPin, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const stripPrefix = (name) => (name || '').replace(/^FE\s+/i, '');

const navItems = [
    { to: '/guide', icon: Home, label: 'Accueil', end: true },
    { to: '/guide/recettes', icon: BookOpen, label: 'Recettes' },
    { to: '/guide/evaluations', icon: Star, label: 'Eval' },
    { to: '/guide/haccp', icon: ShieldCheck, label: 'HACCP' },
    { to: '/guide/checklist', icon: ClipboardList, label: 'Fiches' },
];

export default function GuideLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedSite, setSelectedSite] = useState(null);
    const [sites, setSites] = useState([]);
    const [showSitePicker, setShowSitePicker] = useState(false);
    const pickerRef = useRef(null);

    // Move Emergent badge above bottom nav
    useEffect(() => {
        const badge = document.getElementById('emergent-badge');
        if (badge) badge.style.bottom = '80px';
        return () => { if (badge) badge.style.bottom = '20px'; };
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('guide_selected_site');
        if (stored) {
            try { setSelectedSite(JSON.parse(stored)); } catch {}
        }
    }, [location.pathname]);

    useEffect(() => {
        const loadSites = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('guide_user') || '{}');
                const guideSites = user.guide_sites || [];
                if (guideSites.length <= 1) return;
                const token = localStorage.getItem('guide_token') || localStorage.getItem('token');
                const res = await fetch(`${API}/api/sites`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                });
                const allSites = await res.json();
                setSites(allSites.filter(s => guideSites.includes(s.id)));
            } catch {}
        };
        loadSites();
    }, []);

    useEffect(() => {
        const handleClick = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowSitePicker(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const changeSite = (site) => {
        setSelectedSite(site);
        localStorage.setItem('guide_selected_site', JSON.stringify(site));
        setShowSitePicker(false);
        toast.success(`Site: ${site.name}`);
    };

    const handleLogout = () => {
        localStorage.removeItem('guide_token');
        localStorage.removeItem('guide_user');
        localStorage.removeItem('guide_selected_site');
        navigate('/guide/login');
    };

    return (
        <div className="min-h-screen bg-slate-200 flex justify-center" data-testid="guide-layout">
            <div className="w-full max-w-[480px] bg-slate-50 min-h-screen flex flex-col relative shadow-xl">
                {/* Top bar */}
                <header className="bg-gradient-to-r from-red-700 to-red-600 text-white px-4 py-3 flex items-center justify-between shadow-lg sticky top-0 z-50">
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-bold tracking-tight">Fuji Express</span>
                        {selectedSite ? (
                            <div className="relative" ref={pickerRef}>
                                <button
                                    onClick={() => sites.length > 1 && setShowSitePicker(!showSitePicker)}
                                    className={`flex items-center gap-1 text-sm bg-white/15 px-2.5 py-1 rounded-lg ${sites.length > 1 ? 'hover:bg-white/25 cursor-pointer' : ''}`}
                                    data-testid="site-selector-btn"
                                >
                                    <MapPin className="w-3 h-3" />
                                    <span className="max-w-[120px] truncate">{stripPrefix(selectedSite.name)}</span>
                                    {sites.length > 1 && <ChevronDown className="w-3 h-3" />}
                                </button>
                                {showSitePicker && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden min-w-[180px] z-50">
                                        {sites.map(site => (
                                            <button
                                                key={site.id}
                                                onClick={() => changeSite(site)}
                                                className={`w-full px-4 py-3 text-left text-base flex items-center gap-2 hover:bg-slate-50 transition-colors ${
                                                    selectedSite?.id === site.id ? 'bg-red-50 text-red-700 font-medium' : 'text-slate-700'
                                                }`}
                                                data-testid={`site-picker-${site.id}`}
                                            >
                                                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                                {stripPrefix(site.name)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">Guide</span>
                        )}
                    </div>
                    <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="guide-logout-btn">
                        <LogOut className="w-5 h-5" />
                    </button>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto pb-20">
                    <Outlet />
                </main>

                {/* Bottom navigation */}
                <nav className="fixed bottom-0 w-full max-w-[480px] bg-white border-t border-slate-200 flex justify-around py-2 px-1 z-50 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                    {navItems.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) =>
                                `flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
                                    isActive
                                        ? 'text-red-600'
                                        : 'text-slate-400 hover:text-slate-600'
                                }`
                            }
                            data-testid={`guide-nav-${item.label.toLowerCase()}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="text-[10px] font-medium">{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
            </div>
        </div>
    );
}
